import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Box, Typography, Button, Stack } from '@mui/material'
import { ROUTES } from '@/src/utils/routes'

interface Props {
  children?: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  redirectToErrorPage?: boolean
  showReloadButton?: boolean
  showTryAgainButton?: boolean
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Store error info for display
    this.setState({ errorInfo })

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // Optionally redirect to error page
    if (this.props.redirectToErrorPage && typeof window !== 'undefined') {
      // Small delay to ensure error state is set
      setTimeout(() => {
        window.location.href = ROUTES.SERVER_ERROR
      }, 100)
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleGoHome = () => {
    window.location.href = ROUTES.HOME
  }

  private shouldShowTryAgainButton = (): boolean => {
    if (this.props.showTryAgainButton !== undefined) {
      return this.props.showTryAgainButton
    }

    return true
  }

  private shouldShowReloadButton = (): boolean => {
    if (this.props.showReloadButton !== undefined) {
      return this.props.showReloadButton
    }

    return true
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            p: 4,
            textAlign: 'center'
          }}
        >
          <Typography variant='h5' color='error' gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3, maxWidth: 600 }}>
            {this.state.error?.message || 'An unexpected error occurred'}
          </Typography>

          {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'error.light',
                borderRadius: 1,
                maxWidth: 800,
                textAlign: 'left',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                overflow: 'auto',
                maxHeight: 300
              }}
            >
              <Typography variant='caption' component='pre' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {this.state.error?.stack || this.state.errorInfo.componentStack}
              </Typography>
            </Box>
          )}

          <Stack direction='row' spacing={2}>
            {this.shouldShowTryAgainButton() && (
              <Button variant='outlined' onClick={this.handleReset}>
                Try Again
              </Button>
            )}
            {this.shouldShowReloadButton() && (
              <Button variant='contained' onClick={this.handleReload}>
                Reload Page
              </Button>
            )}
            <Button variant='outlined' onClick={this.handleGoHome}>
              Go Home
            </Button>
          </Stack>
        </Box>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
